import type { ListPurchaseLinesInput } from '@obraplan/core/application/contracts/purchase-lines';

/**
 * O filtro do cronograma de materiais (Fase 26): torre, pavimento, disciplina e serviço.
 *
 * A lista é paginada **no servidor** (`ListPurchaseLines`), e por isso o filtro também vai ao
 * servidor — filtrar só a página que chegou diria que não há o que está na página seguinte. O
 * que mora aqui é a conta de exibição: as opções de cada caixa, que dependem das outras, e o
 * pedido que o filtro e a página viram.
 *
 * Os quatro campos são ids; vazio é "todos".
 */

export interface PurchaseFilter {
  readonly towerId: string;
  readonly floorId: string;
  readonly disciplineId: string;
  readonly serviceId: string;
}

export const NO_PURCHASE_FILTER: PurchaseFilter = {
  towerId: '',
  floorId: '',
  disciplineId: '',
  serviceId: '',
};

export function isFiltered(filter: PurchaseFilter): boolean {
  return (
    filter.towerId !== '' ||
    filter.floorId !== '' ||
    filter.disciplineId !== '' ||
    filter.serviceId !== ''
  );
}

/**
 * Muda um campo e zera os que dependem dele: trocar a torre invalida o pavimento, trocar o
 * pavimento ou a torre invalida a disciplina (que sai das frentes do recorte), e trocar a
 * disciplina invalida o serviço. Escolha que sobrevive a uma troca de recorte vira lista
 * vazia sem explicação — pior do que recomeçar a caixa de baixo.
 */
export function changeFilter(
  filter: PurchaseFilter,
  field: keyof PurchaseFilter,
  value: string,
): PurchaseFilter {
  switch (field) {
    case 'towerId':
      return { towerId: value, floorId: '', disciplineId: '', serviceId: '' };
    case 'floorId':
      return { ...filter, floorId: value, disciplineId: '', serviceId: '' };
    case 'disciplineId':
      return { ...filter, disciplineId: value, serviceId: '' };
    case 'serviceId':
      return { ...filter, serviceId: value };
  }
}

/** O pedido ao servidor: os campos vazios não vão. */
export function toListInput(
  projectId: string,
  filter: PurchaseFilter,
  page: number,
  pageSize: number,
): ListPurchaseLinesInput {
  return {
    projectId,
    ...(filter.towerId === '' ? {} : { towerId: filter.towerId }),
    ...(filter.floorId === '' ? {} : { floorId: filter.floorId }),
    ...(filter.disciplineId === '' ? {} : { disciplineId: filter.disciplineId }),
    ...(filter.serviceId === '' ? {} : { serviceId: filter.serviceId }),
    page,
    pageSize,
  };
}

/** Quantas páginas o total pede; sempre pelo menos uma. */
export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

// ── As opções de cada caixa ─────────────────────────────────────────────────────────

/** O que as opções precisam da árvore da obra — só os nomes e os vínculos. */
export interface FilterTower {
  readonly tower: { readonly id: string; readonly name: string };
  readonly floors: readonly {
    readonly floor: { readonly id: string; readonly name: string };
    readonly activities: readonly {
      readonly discipline: { readonly id: string; readonly name: string };
    }[];
  }[];
}

export interface FilterService {
  readonly id: string;
  readonly name: string;
  readonly disciplineId: string;
}

export interface FilterOption {
  readonly id: string;
  readonly label: string;
}

export interface FilterOptions {
  readonly towers: readonly FilterOption[];
  readonly floors: readonly FilterOption[];
  readonly disciplines: readonly FilterOption[];
  readonly services: readonly FilterOption[];
}

function byLabel(a: FilterOption, b: FilterOption): number {
  return a.label.localeCompare(b.label, 'pt-BR');
}

/**
 * As opções de cada caixa, **dependentes** das outras.
 *
 * - **Pavimento**: os da torre escolhida, na ordem do andar que a árvore já traz; sem torre,
 *   todos, com o nome da torre antes quando a obra tem mais de uma (o "1º Pavimento" se
 *   repete);
 * - **Disciplina**: as que têm frente no recorte de torre e pavimento — o catálogo inteiro
 *   levaria a escolhas que dão lista vazia;
 * - **Serviço**: os da disciplina escolhida; sem disciplina, os das disciplinas do recorte,
 *   com o nome dela antes (o mesmo nome de serviço existe em mais de uma).
 */
export function buildFilterOptions(
  towers: readonly FilterTower[],
  services: readonly FilterService[],
  filter: PurchaseFilter,
): FilterOptions {
  const manyTowers = towers.length > 1;
  const inTower = towers.filter(
    (entry) => filter.towerId === '' || entry.tower.id === filter.towerId,
  );

  const floors: FilterOption[] = inTower.flatMap((entry) =>
    entry.floors.map((floorNode) => ({
      id: floorNode.floor.id,
      label:
        manyTowers && filter.towerId === ''
          ? `${entry.tower.name} · ${floorNode.floor.name}`
          : floorNode.floor.name,
    })),
  );

  const scopeDisciplines = new Map<string, string>();
  for (const entry of inTower) {
    for (const floorNode of entry.floors) {
      if (filter.floorId !== '' && floorNode.floor.id !== filter.floorId) continue;
      for (const activity of floorNode.activities) {
        scopeDisciplines.set(activity.discipline.id, activity.discipline.name);
      }
    }
  }
  const disciplines: FilterOption[] = [...scopeDisciplines].map(([id, label]) => ({ id, label }));

  const wantedDisciplines =
    filter.disciplineId === '' ? scopeDisciplines : new Map([[filter.disciplineId, '']]);
  const showDiscipline = filter.disciplineId === '';
  const serviceOptions: FilterOption[] = services
    .filter((service) => wantedDisciplines.has(service.disciplineId))
    .map((service) => ({
      id: service.id,
      label: showDiscipline
        ? `${wantedDisciplines.get(service.disciplineId) ?? ''} · ${service.name}`
        : service.name,
    }));

  return {
    towers: towers.map((entry) => ({ id: entry.tower.id, label: entry.tower.name })),
    floors,
    disciplines: disciplines.sort(byLabel),
    services: serviceOptions.sort(byLabel),
  };
}

/** O recorte em palavras, para o nome do arquivo: `"TORRE 2 1º Pavimento ALVENARIA"`. */
export function describeFilter(filter: PurchaseFilter, options: FilterOptions): string {
  const labelOf = (list: readonly FilterOption[], id: string): string | undefined =>
    id === '' ? undefined : list.find((option) => option.id === id)?.label;
  return [
    labelOf(options.towers, filter.towerId),
    labelOf(options.floors, filter.floorId),
    labelOf(options.disciplines, filter.disciplineId),
    labelOf(options.services, filter.serviceId),
  ]
    .filter((part): part is string => part !== undefined)
    .join(' ');
}
